/**
 * SRD Compendium — Mapping Utilities
 *
 * D&D 5e System Reference Document 5.1
 * Licensed under Creative Commons Attribution 4.0 International (CC-BY-4.0)
 */

import type { CharacterSheet, SerializedToken } from '../../../types.js';
import type { SrdMonster } from './types.js';
import { DND5E_TEMPLATE_ID } from '../../../builtinTemplates.js';

// ---- CR helpers ----

/** Format a numeric CR as a string (0, 1/8, 1/4, 1/2, 1…30). */
export function formatCr(cr: number): string {
  if (cr === 0) return '0';
  if (cr === 0.125) return '1/8';
  if (cr === 0.25) return '1/4';
  if (cr === 0.5) return '1/2';
  return String(cr);
}

/** Estimate a CR-appropriate "level" for the character sheet level field. */
function crToLevel(cr: number): number {
  if (cr <= 0) return 1;
  if (cr <= 0.5) return 1;
  if (cr <= 1) return 1;
  if (cr <= 4) return Math.round(cr * 2);
  if (cr <= 20) return Math.min(20, Math.round(cr));
  return 20;
}

// ---- Ability modifier ----

function abilityMod(score: number): number {
  return Math.floor((score - 10) / 2);
}

// ---- Token size ----

/**
 * Map SRD size to grid-square token size.
 * Tiny=0.5, Small/Medium=1, Large=2, Huge=3, Gargantuan=4
 */
export function sizeToTokenSize(size: SrdMonster['size']): number {
  switch (size) {
    case 'Tiny': return 0.5;
    case 'Small': return 1;
    case 'Medium': return 1;
    case 'Large': return 2;
    case 'Huge': return 3;
    case 'Gargantuan': return 4;
  }
}

// ---- Type-to-colour mapping ----

/** Map a monster type string to a representative token colour hex. */
export function typeToColor(type: string): string {
  const t = type.toLowerCase();
  if (t.includes('dragon')) return '#dc2626';     // red
  if (t.includes('undead')) return '#7c3aed';     // purple
  if (t.includes('beast')) return '#16a34a';      // green
  if (t.includes('fiend')) return '#991b1b';      // dark red
  if (t.includes('celestial')) return '#d97706';  // amber
  if (t.includes('elemental')) return '#ea580c';  // orange
  if (t.includes('fey')) return '#db2777';        // pink
  if (t.includes('giant')) return '#92400e';      // brown
  if (t.includes('aberration')) return '#0d9488'; // teal
  if (t.includes('construct')) return '#6b7280';  // gray
  if (t.includes('monstrosity')) return '#15803d';// dark green
  if (t.includes('ooze')) return '#65a30d';       // lime
  if (t.includes('plant')) return '#166534';      // forest green
  if (t.includes('humanoid')) return '#2563eb';   // blue
  return '#64748b'; // slate
}

// ---- Token description ----

/** Build a short stat-block summary for the token hover card. */
export function buildTokenDescription(monster: SrdMonster): string {
  const cr = formatCr(monster.cr);
  const type = monster.subtype
    ? `${monster.type} (${monster.subtype})`
    : monster.type;
  const size = monster.size;
  const mods = monster.abilities;
  const modStr = (v: number) => {
    const m = abilityMod(v);
    return m >= 0 ? `+${m}` : String(m);
  };
  return (
    `${size} ${type} | CR ${cr} | AC ${monster.ac} | HP ${monster.hp}\n` +
    `STR ${mods.str}(${modStr(mods.str)}) DEX ${mods.dex}(${modStr(mods.dex)}) ` +
    `CON ${mods.con}(${modStr(mods.con)}) INT ${mods.int}(${modStr(mods.int)}) ` +
    `WIS ${mods.wis}(${modStr(mods.wis)}) CHA ${mods.cha}(${modStr(mods.cha)})`
  );
}

// ---- Aura extraction ----

const AURA_PATTERN = /\baura\b/i;
const AURA_RADIUS = /(\d+)[- ]?(?:foot|feet|ft)/i;

/** Detect aura traits and return TokenAura-compatible structures. */
export function extractAuras(monster: SrdMonster): Array<{ name: string; radiusFt: number }> {
  const result: Array<{ name: string; radiusFt: number }> = [];
  const features = [...(monster.traits ?? []), ...(monster.actions ?? [])];
  for (const f of features) {
    if (AURA_PATTERN.test(f.name) || AURA_PATTERN.test(f.description)) {
      const m = AURA_RADIUS.exec(f.description);
      const radiusFt = m ? parseInt(m[1], 10) : 5;
      result.push({ name: f.name, radiusFt });
    }
  }
  return result;
}

// ---- monsterToCharacterSheet ----

/**
 * Convert an SrdMonster to a D&D 5e CharacterSheet.
 * Maps to the builtin `dnd5eTemplate` field keys.
 */
export function monsterToCharacterSheet(monster: SrdMonster): CharacterSheet {
  const { abilities: ab } = monster;

  const strMod = abilityMod(ab.str);
  const dexMod = abilityMod(ab.dex);
  const conMod = abilityMod(ab.con);
  const intMod = abilityMod(ab.int);
  const wisMod = abilityMod(ab.wis);
  const chaMod = abilityMod(ab.cha);
  const level = crToLevel(monster.cr);
  const profBonus = Math.floor((level - 1) / 4) + 2;
  const initiative = dexMod;

  // Saving throw / skill proficiencies: the proficiency level the sheet values hold (0 none, 1 proficient) — a boolean
  // is refused by the sheet values schema, so a sheet from an SRD monster was never created (create_npc_with_sheet).
  const lvl = (has: boolean): 0 | 1 => (has ? 1 : 0);
  // Saving throw proficiencies
  const st = monster.savingThrows ?? {};
  const strSaveProf = st.str !== undefined;
  const dexSaveProf = st.dex !== undefined;
  const conSaveProf = st.con !== undefined;
  const intSaveProf = st.int !== undefined;
  const wisSaveProf = st.wis !== undefined;
  const chaSaveProf = st.cha !== undefined;

  // Skill proficiencies — detect from monster.skills
  const skills = monster.skills ?? {};
  const hasSkill = (key: string) => key in skills;

  // Build repeating attacks from actions
  const attackRows = (monster.actions ?? [])
    .filter(a => a.attackBonus !== undefined || a.damageDice !== undefined)
    .map(a => ({
      rowId: globalThis.crypto.randomUUID(),
      values: {
        name: a.name,
        to_hit: a.attackBonus ?? 0,
        damage_dice: a.damageDice ?? '1d6',
        damage_type: a.damageType ?? 'bludgeoning',
        properties: a.reach ? `reach ${a.reach} ft.` : '',
      },
    }));

  const values: Record<string, string | number | boolean | string[]> = {
    name: monster.name,
    race: `${monster.size} ${monster.type}`,
    class_name: 'Monster',
    level,
    background: '',
    alignment: monster.alignment,
    xp: monster.xp,
    // Ability scores
    str: ab.str,
    dex: ab.dex,
    con: ab.con,
    int: ab.int,
    wis: ab.wis,
    cha: ab.cha,
    // Computed ability mods (SheetEngine can compute these but we pre-fill)
    str_mod: strMod,
    dex_mod: dexMod,
    con_mod: conMod,
    int_mod: intMod,
    wis_mod: wisMod,
    cha_mod: chaMod,
    prof_bonus: profBonus,
    initiative,
    // Combat
    ac: monster.ac,
    hp: monster.hp,
    max_hp: monster.hp,
    temp_hp: 0,
    hit_dice: monster.hitDice,
    hit_dice_used: 0,
    speed: monster.speed.walk,
    death_save_success: 0,
    death_save_fail: 0,
    conditions: [],
    // Saving throws
    str_save_prof: lvl(strSaveProf),
    dex_save_prof: lvl(dexSaveProf),
    con_save_prof: lvl(conSaveProf),
    int_save_prof: lvl(intSaveProf),
    wis_save_prof: lvl(wisSaveProf),
    cha_save_prof: lvl(chaSaveProf),
    // Skills
    acrobatics_prof: lvl(hasSkill('acrobatics')),
    animal_handling_prof: lvl(hasSkill('animal_handling') || hasSkill('animal handling')),
    arcana_prof: lvl(hasSkill('arcana')),
    athletics_prof: lvl(hasSkill('athletics')),
    deception_prof: lvl(hasSkill('deception')),
    history_prof: lvl(hasSkill('history')),
    insight_prof: lvl(hasSkill('insight')),
    intimidation_prof: lvl(hasSkill('intimidation')),
    investigation_prof: lvl(hasSkill('investigation')),
    medicine_prof: lvl(hasSkill('medicine')),
    nature_prof: lvl(hasSkill('nature')),
    perception_prof: lvl(hasSkill('perception')),
    performance_prof: lvl(hasSkill('performance')),
    persuasion_prof: lvl(hasSkill('persuasion')),
    religion_prof: lvl(hasSkill('religion')),
    sleight_of_hand_prof: lvl(hasSkill('sleight_of_hand') || hasSkill('sleight of hand')),
    stealth_prof: lvl(hasSkill('stealth')),
    survival_prof: lvl(hasSkill('survival')),
    passive_perception: monster.senses.passivePerception,
    // Notes
    personality_traits: '',
    ideals: '',
    bonds: '',
    flaws: '',
    notes: '',
  };

  return {
    id: globalThis.crypto.randomUUID(),
    templateId: DND5E_TEMPLATE_ID,
    sheetType: 'npc',
    name: monster.name,
    ownerName: null,
    values,
    abilities: [],
    publicFields: [],
    repeatingData: attackRows.length > 0 ? { attacks: attackRows } : {},
    tokenActions: [],
    portrait: null,
    tokenDescription: buildTokenDescription(monster),
    activeEffects: [],
    dnd5e: {
      spellSlots: null,
      hitDice: {
        total: level,
        used: 0,
        dieType: 8,
      },
      spellbook: [],
      concentrationSpellId: null,
      classData: [],
      deathSaveState: { status: 'alive', successes: 0, failures: 0 },
    },
    visible: false,
  };
}

// ---- monsterToToken ----

/**
 * Convert an SrdMonster to a (partial) SerializedToken.
 * The caller must supply `id`, and the `pageId` is handled by the session layer.
 */
export function monsterToToken(
  monster: SrdMonster,
  x: number,
  y: number,
): Omit<SerializedToken, 'id'> {
  return {
    name: monster.name,
    x,
    y,
    size: sizeToTokenSize(monster.size),
    color: typeToColor(monster.type),
    imageSrc: null,
    hp: monster.hp,
    maxHp: monster.hp,
    ac: monster.ac,
    conditions: [],
    description: buildTokenDescription(monster),
    notes: '',
    visible: true,
    auras: [],
  };
}
